import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronRight, ChevronDown, ChevronLeft, FolderPlus, FilePlus, MoreHorizontal, Trash2,
  Pencil, FolderOpen, FileText, Save, FolderClosed, GripVertical, Search,
  CalendarPlus, PanelRight, Command, ToggleLeft, Eye, Code2,
} from "lucide-react";
import Markdown from "@/components/Markdown";
import MarkdownEditor from "@/components/MarkdownEditor";
import RichEditor from "@/components/RichEditor";
import PropertiesPanel from "@/components/PropertiesPanel";
import LinksPanel from "@/components/LinksPanel";
import VaultPalette from "@/components/VaultPalette";
import VaultGraph from "@/pages/VaultGraph";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EXPAND_KEY = "disciple_vault_expanded";

// Notes with Obsidian-specific syntax that the Visual editor can't round-trip
// safely (callouts) open in Markdown source mode by default — "do no harm".
function hasAdvancedSyntax(md) {
  return /(^|\n)\s*>\s*\[!/.test(md || "");
}

function loadExpanded() {
  try { return new Set(JSON.parse(localStorage.getItem(EXPAND_KEY) || "[]")); }
  catch { return new Set(); }
}
function saveExpanded(set) {
  try { localStorage.setItem(EXPAND_KEY, JSON.stringify([...set])); } catch {}
}

function buildTree(folders, docs) {
  // Build a hierarchical structure { name, path, children: [...], docs: [...] }
  const root = { name: "", path: "", children: new Map(), docs: [] };
  const ensure = (path) => {
    if (!path) return root;
    const parts = path.split("/");
    let node = root;
    for (let i = 0; i < parts.length; i++) {
      const seg = parts[i];
      if (!node.children.has(seg)) {
        node.children.set(seg, {
          name: seg,
          path: parts.slice(0, i + 1).join("/"),
          children: new Map(),
          docs: [],
        });
      }
      node = node.children.get(seg);
    }
    return node;
  };
  for (const f of folders) ensure(f.path);
  for (const d of docs) {
    const node = ensure(d.folder || "");
    node.docs.push(d);
  }
  // Convert Map → sorted array; sort docs by name
  const finalize = (n) => {
    const kids = [...n.children.values()].sort((a, b) => a.name.localeCompare(b.name));
    kids.forEach(finalize);
    n.children = kids;
    n.docs.sort((a, b) => a.name.localeCompare(b.name));
  };
  finalize(root);
  return root;
}

function flattenTree(root, expanded) {
  // DFS into an ordered list of { kind, depth, ... } rows respecting expanded state.
  const rows = [];
  const walk = (node, depth) => {
    for (const child of node.children) {
      rows.push({ kind: "folder", depth, node: child });
      if (expanded.has(child.path)) walk(child, depth + 1);
    }
    for (const d of node.docs) {
      rows.push({ kind: "doc", depth, doc: d });
    }
  };
  walk(root, 0);
  return rows;
}

export default function Vault() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeId, setActiveId] = useState(docId || null);
  const [mode, setMode] = useState("list");
  const [editing, setEditing] = useState(false);
  const [editorText, setEditorText] = useState("");
  const [editorMode, setEditorMode] = useState("visual");
  const [expanded, setExpanded] = useState(loadExpanded);
  // Context menu & rename
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, kind, target }
  const [renameTarget, setRenameTarget] = useState(null); // { kind, id?, path?, name }
  // Palette (quick switcher / command palette / search) + links panel
  const [palette, setPalette] = useState(null); // { mode: 'switcher' | 'command' | 'search' }
  const [showLinks, setShowLinks] = useState(true);

  const reload = useCallback(async () => {
    const [d, f] = await Promise.all([
      api.get("/vault/docs"),
      api.get("/vault/folders"),
    ]);
    setDocs(d.data);
    setFolders(f.data);
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { if (docId) setActiveId(docId); }, [docId]);

  // Auto-expand all root folders on first load
  useEffect(() => {
    if (folders.length === 0) return;
    if (expanded.size > 0) return;
    const rootPaths = new Set();
    for (const f of folders) rootPaths.add(f.path.split("/")[0]);
    setExpanded(rootPaths);
    saveExpanded(rootPaths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders.length]);

  const activeDoc = useMemo(() => docs.find((d) => d.id === activeId) || null, [docs, activeId]);
  const tree = useMemo(() => buildTree(folders, docs), [folders, docs]);

  useEffect(() => {
    setEditing(false);
    setEditorText(activeDoc?.markdown || "");
    setEditorMode(hasAdvancedSyntax(activeDoc?.markdown || "") ? "markdown" : "visual");
  }, [activeId, activeDoc?.id]);

  // Close context menu on outside click
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [ctxMenu]);

  const toggleFolder = (path) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      saveExpanded(next);
      return next;
    });
  };

  // ---- Actions ----
  const createNote = async (folderPath) => {
    const name = prompt("New note name", "Untitled");
    if (!name) return;
    try {
      const body = {
        id: undefined, user_id: "x", folder: folderPath || "",
        name, markdown: `# ${name}\n\n`, links: [], tags: [],
      };
      const r = await api.post("/vault/docs", body);
      await reload();
      setActiveId(r.data.id);
      navigate(`/vault/${r.data.id}`);
      toast.success(`Created "${name}".`);
    } catch {
      toast.error("Couldn't create note.");
    }
  };

  const createFolder = async (parentPath) => {
    const name = prompt(`New folder in ${parentPath || "root"}`, "New folder");
    if (!name) return;
    const path = parentPath ? `${parentPath}/${name}` : name;
    try {
      await api.post("/vault/folders", { path });
      setExpanded((prev) => { const n = new Set(prev); n.add(path); saveExpanded(n); return n; });
      await reload();
      toast.success(`Folder created.`);
    } catch {
      toast.error("Couldn't create folder.");
    }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/vault/docs/${doc.id}`);
      if (activeId === doc.id) { setActiveId(null); navigate("/vault"); }
      await reload();
      toast.success("Deleted.");
    } catch { toast.error("Couldn't delete."); }
  };

  const deleteFolder = async (path) => {
    if (!window.confirm(`Delete folder "${path}" and everything inside? This cannot be undone.`)) return;
    try {
      await api.delete("/vault/folders", { data: { path } });
      await reload();
      toast.success("Folder deleted.");
    } catch { toast.error("Couldn't delete folder."); }
  };

  const renameDocSubmit = async (doc, newName) => {
    if (!newName || newName === doc.name) { setRenameTarget(null); return; }
    try {
      await api.put(`/vault/docs/${doc.id}`, { name: newName });
      await reload();
      setRenameTarget(null);
    } catch { toast.error("Couldn't rename."); }
  };

  const renameFolderSubmit = async (oldPath, newLeaf) => {
    if (!newLeaf) { setRenameTarget(null); return; }
    const parts = oldPath.split("/");
    parts[parts.length - 1] = newLeaf;
    const newPath = parts.join("/");
    if (newPath === oldPath) { setRenameTarget(null); return; }
    try {
      await api.post("/vault/folders/rename", { old_path: oldPath, new_path: newPath });
      // Bump expanded set entry to new path
      setExpanded((prev) => {
        const next = new Set();
        for (const p of prev) {
          if (p === oldPath || p.startsWith(oldPath + "/")) {
            next.add(newPath + p.slice(oldPath.length));
          } else next.add(p);
        }
        saveExpanded(next);
        return next;
      });
      await reload();
      setRenameTarget(null);
    } catch { toast.error("Couldn't rename folder."); }
  };

  const moveDoc = async (docId, newFolder) => {
    try {
      await api.post("/vault/move", { item_type: "doc", id: docId, new_folder: newFolder });
      await reload();
    } catch { toast.error("Couldn't move."); }
  };
  const moveFolder = async (path, newParent) => {
    if (path === newParent) return;
    // Prevent moving a folder into itself or a descendant
    if (newParent === path || newParent.startsWith(path + "/")) {
      toast.error("Can't move a folder into itself.");
      return;
    }
    try {
      await api.post("/vault/move", { item_type: "folder", path, new_folder: newParent });
      await reload();
    } catch { toast.error("Couldn't move folder."); }
  };

  const saveDoc = async () => {
    if (!activeDoc) return;
    try {
      const r = await api.put(`/vault/docs/${activeDoc.id}`, { markdown: editorText });
      setDocs((prev) => prev.map((d) => (d.id === activeDoc.id ? r.data : d)));
      setEditing(false);
      toast.success("Saved.");
    } catch { toast.error("Couldn't save."); }
  };

  const onSelectGraphNode = (id) => {
    setActiveId(id);
    setMode("list");
    navigate(`/vault/${id}`);
  };

  const openDoc = (id) => {
    if (!id) return;
    setActiveId(id);
    setMode("list");
    navigate(`/vault/${id}`);
  };

  // Turn unlinked mention(s) into [[links]] in another doc, then refresh.
  const linkMention = async (id, newMarkdown, count = 1) => {
    try {
      await api.put(`/vault/docs/${id}`, { markdown: newMarkdown });
      await reload();
      toast.success(count > 1 ? `Linked ${count} mentions.` : "Linked.");
    } catch { toast.error("Couldn't create the link."); }
  };

  // Link every unlinked mention across all listed notes at once.
  const linkAllUnlinked = async (updates, total) => {
    if (!updates.length) return;
    try {
      await Promise.all(updates.map((u) => api.put(`/vault/docs/${u.id}`, { markdown: u.markdown })));
      await reload();
      toast.success(`Linked ${total} mention${total === 1 ? "" : "s"} across ${updates.length} note${updates.length === 1 ? "" : "s"}.`);
    } catch { toast.error("Couldn't link all mentions."); }
  };

  // Open (or create) today's daily note from a template, in the "Daily" folder.
  const openDailyNote = async () => {
    const now = new Date();
    const name = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const folder = "Daily";
    const existing = docs.find((d) => d.folder === folder && d.name === name);
    if (existing) { openDoc(existing.id); return; }
    try { await api.post("/vault/folders", { path: folder }); } catch {}
    const human = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const md = `---\ntype: daily\ndate: ${name}\n---\n# ${human}\n\n## Focus\n- \n\n## Notes\n\n\n## Tasks\n- [ ] \n`;
    try {
      const r = await api.post("/vault/docs", { id: undefined, user_id: "x", folder, name, markdown: md, links: [], tags: ["daily"] });
      setExpanded((prev) => { const n = new Set(prev); n.add(folder); saveExpanded(n); return n; });
      await reload();
      openDoc(r.data.id);
      toast.success("Daily note ready.");
    } catch { toast.error("Couldn't create the daily note."); }
  };

  // Command palette actions
  const commands = useMemo(() => [
    { id: "new-note", label: "New note", icon: FilePlus, hint: "in root", run: () => createNote("") },
    { id: "new-folder", label: "New folder", icon: FolderPlus, hint: "in root", run: () => createFolder("") },
    { id: "daily", label: "Open today's daily note", icon: CalendarPlus, run: openDailyNote },
    { id: "search", label: "Search all notes", icon: Search, hint: "⌘⇧F", run: () => setPalette({ mode: "search" }) },
    { id: "switcher", label: "Quick switcher — jump to a note", icon: FileText, hint: "⌘O", run: () => setPalette({ mode: "switcher" }) },
    { id: "graph", label: "Open graph view", icon: ToggleLeft, run: () => setMode("graph") },
    { id: "list", label: "Open list view", icon: ToggleLeft, run: () => setMode("list") },
    { id: "links", label: "Toggle links panel", icon: PanelRight, run: () => setShowLinks((v) => !v) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [docs]);

  // Keyboard shortcuts: Quick switcher / Command palette / Search
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      if (k === "o" && !e.shiftKey) { e.preventDefault(); setPalette({ mode: "switcher" }); }
      else if (k === "p" && !e.shiftKey) { e.preventDefault(); setPalette({ mode: "command" }); }
      else if (k === "f" && e.shiftKey) { e.preventDefault(); setPalette({ mode: "search" }); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Context menu items
  const ctxItems = ctxMenu ? (() => {
    const { kind, target } = ctxMenu;
    if (kind === "folder") {
      return [
        { label: "New note here", icon: FilePlus, onClick: () => createNote(target.path) },
        { label: "New folder", icon: FolderPlus, onClick: () => createFolder(target.path) },
        { label: "Rename", icon: Pencil, onClick: () => setRenameTarget({ kind: "folder", path: target.path, name: target.name }) },
        { label: "Delete", icon: Trash2, danger: true, onClick: () => deleteFolder(target.path) },
      ];
    }
    if (kind === "doc") {
      return [
        { label: "Rename", icon: Pencil, onClick: () => setRenameTarget({ kind: "doc", id: target.id, name: target.name }) },
        { label: "Delete", icon: Trash2, danger: true, onClick: () => deleteDoc(target) },
      ];
    }
    return [];
  })() : [];

  return (
    <>
      <div className="surface-header">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="surface-title">Vault</div>
          <span className="text-dim" style={{ fontSize: 12 }}>· local-first · never trained on</span>
        </div>
        <div className="seg" data-testid="vault-seg">
          <button className={mode === "list" ? "active" : ""} onClick={() => setMode("list")} data-testid="vault-list-btn">List</button>
          <button className={mode === "graph" ? "active" : ""} onClick={() => setMode("graph")} data-testid="vault-graph-btn">Graph</button>
        </div>
      </div>

      {mode === "list" ? (
        <div className={`vault-split ${activeDoc ? "has-active" : ""}`} style={{ flex: 1, display: "flex", minHeight: 0 }}>
          {/* Tree */}
          <div
            className="vault-tree-pane"
            style={{ width: 300, borderRight: "1px solid var(--hairline-soft)", overflowY: "auto", padding: "10px 6px 24px", flexShrink: 0 }}
            data-testid="vault-tree"
            onContextMenu={(e) => {
              if (e.target === e.currentTarget || e.target.closest('[data-rootmenu="1"]')) {
                e.preventDefault();
                setCtxMenu({ x: e.clientX, y: e.clientY, kind: "folder", target: { path: "", name: "Vault" } });
              }
            }}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
            onDrop={(e) => {
              e.preventDefault();
              try {
                const data = JSON.parse(e.dataTransfer.getData("application/x-disciple") || "{}");
                if (data.kind === "doc") moveDoc(data.id, "");
                else if (data.kind === "folder") moveFolder(data.path, "");
              } catch {}
            }}
          >
            {/* Toolbar */}
            <div data-rootmenu="1" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 10px 10px" }}>
              <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ph-accent-purple)", fontWeight: 600 }}>Vault</div>
              <div style={{ display: "flex", gap: 4 }}>
                <button className="btn btn-ghost" style={{ padding: 6 }} onClick={() => setPalette({ mode: "search" })} title="Search all notes (⌘⇧F)" data-testid="vault-search-btn">
                  <Search size={16} strokeWidth={2} />
                </button>
                <button className="btn btn-ghost" style={{ padding: 6 }} onClick={openDailyNote} title="Open today's daily note" data-testid="daily-note-btn">
                  <CalendarPlus size={16} strokeWidth={2} />
                </button>
                <button className="btn btn-ghost" style={{ padding: 6 }} onClick={() => createNote("")} title="New note (root)" data-testid="new-doc-root">
                  <FilePlus size={16} strokeWidth={2} />
                </button>
                <button className="btn btn-ghost" style={{ padding: 6 }} onClick={() => createFolder("")} title="New folder (root)" data-testid="new-folder-root">
                  <FolderPlus size={16} strokeWidth={2} />
                </button>
              </div>
            </div>
            <div data-testid="tree-rows">
              {flattenTree(tree, expanded).map((row) => {
                if (row.kind === "folder") {
                  const node = row.node;
                  const isOpen = expanded.has(node.path);
                  const renaming = renameTarget?.kind === "folder" && renameTarget.path === node.path;
                  return (
                    <FolderRow
                      key={`f-${node.path}`}
                      node={node}
                      depth={row.depth}
                      isOpen={isOpen}
                      onToggle={() => toggleFolder(node.path)}
                      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY, kind: "folder", target: { path: node.path, name: node.name } }); }}
                      renaming={renaming}
                      onRenameSubmit={(v) => renameFolderSubmit(node.path, v)}
                      onRenameCancel={() => setRenameTarget(null)}
                      moveDoc={moveDoc}
                      moveFolder={moveFolder}
                    />
                  );
                }
                const d = row.doc;
                const renaming = renameTarget?.kind === "doc" && renameTarget.id === d.id;
                return (
                  <DocRow
                    key={`d-${d.id}`}
                    doc={d}
                    depth={row.depth}
                    active={activeId === d.id}
                    onOpen={() => { setActiveId(d.id); navigate(`/vault/${d.id}`); }}
                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY, kind: "doc", target: d }); }}
                    renaming={renaming}
                    onRenameSubmit={(v) => renameDocSubmit(d, v)}
                    onRenameCancel={() => setRenameTarget(null)}
                    moveDoc={moveDoc}
                  />
                );
              })}
            </div>
          </div>

          {/* Reading pane */}
          <div className="vault-reader-pane" style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }} data-testid="vault-reader">
            <div style={{ flex: 1, overflowY: "auto", padding: "28px clamp(14px, 5vw, 40px) 60px", minWidth: 0 }}>
            {activeDoc ? (
              <div style={{ maxWidth: editing ? "100%" : 760, margin: "0 auto" }} className="fade-up">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 16 }}>
                  <button
                    className="btn btn-ghost mobile-only"
                    onClick={() => { setActiveId(null); navigate("/vault"); }}
                    title="All notes"
                    aria-label="Back to all notes"
                    data-testid="vault-back-btn"
                    style={{ padding: 8, flexShrink: 0 }}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div style={{ minWidth: 0 }}>
                    <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                      {activeDoc.folder || "Vault"}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {activeDoc.name}
                    </div>
                  </div>
                  {editing ? (
                    <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
                      <div className="editor-mode-toggle" data-testid="editor-mode-toggle">
                        <button
                          className={`emt-btn ${editorMode === "visual" ? "active" : ""}`}
                          onClick={() => setEditorMode("visual")}
                          title="Format visually — no Markdown"
                          data-testid="editor-mode-visual"
                        ><Eye size={13} /> Visual</button>
                        <button
                          className={`emt-btn ${editorMode === "markdown" ? "active" : ""}`}
                          onClick={() => setEditorMode("markdown")}
                          title="Edit raw Markdown (wiki-links, callouts, embeds)"
                          data-testid="editor-mode-markdown"
                        ><Code2 size={13} /> Markdown</button>
                      </div>
                      <button className="btn btn-ghost" onClick={() => { setEditing(false); setEditorText(activeDoc.markdown); }}>Cancel</button>
                      <button className="btn btn-primary" onClick={saveDoc} data-testid="save-doc-btn"><Save size={14} /> Save</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      <button className={`btn btn-ghost ${showLinks ? "active" : ""}`} onClick={() => setShowLinks((v) => !v)} title="Toggle links panel" data-testid="toggle-links-btn">
                        <PanelRight size={15} />
                      </button>
                      <button className="btn" onClick={() => setEditing(true)} data-testid="edit-doc-btn">Edit</button>
                    </div>
                  )}
                </div>
                {editing ? (
                  editorMode === "visual" ? (
                    <RichEditor
                      value={editorText}
                      onChange={setEditorText}
                      docs={docs}
                      onOpenDoc={openDoc}
                    />
                  ) : (
                    <MarkdownEditor
                      value={editorText}
                      onChange={setEditorText}
                      docs={docs}
                      onOpenDoc={openDoc}
                    />
                  )
                ) : (
                  <>
                    <PropertiesPanel markdown={activeDoc.markdown} />
                    <Markdown text={activeDoc.markdown} docs={docs} onOpenDoc={openDoc} />
                  </>
                )}
                {activeDoc.tags?.length > 0 && (
                  <div style={{ marginTop: 18, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {activeDoc.tags.map((t) => (
                      <span key={t} style={{ fontSize: 11, color: "var(--ink-dim)", border: "1px solid var(--hairline)", borderRadius: 999, padding: "2px 10px" }}>#{t}</span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-dim" style={{ padding: 24 }}>Select a document, or create one with the + button.</div>
            )}
            </div>
            {activeDoc && !editing && showLinks && (
              <LinksPanel doc={activeDoc} docs={docs} onOpen={openDoc} onLink={linkMention} onLinkAll={linkAllUnlinked} />
            )}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, position: "relative" }} data-testid="vault-graph-canvas">
          <VaultGraph docs={docs} onSelect={onSelectGraphNode} />
        </div>
      )}

      {/* Context menu */}
      {ctxMenu && (
        <div
          data-testid="vault-context-menu"
          style={{
            position: "fixed", top: ctxMenu.y, left: ctxMenu.x, zIndex: 1000,
            background: "var(--panel-2)", border: "1px solid var(--hairline)",
            borderRadius: 8, padding: 4, minWidth: 180,
            boxShadow: "0 14px 38px -10px rgba(15,23,42,0.35)",
          }}
        >
          {ctxItems.map((it, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setCtxMenu(null); it.onClick(); }}
              style={{
                width: "100%", textAlign: "left",
                display: "flex", alignItems: "center", gap: 8,
                padding: "8px 10px", borderRadius: 6,
                background: "transparent", border: "none",
                fontSize: 13, color: it.danger ? "var(--danger)" : "var(--ink)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--panel-3)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <it.icon size={13} /> {it.label}
            </button>
          ))}
        </div>
      )}

      {palette && (
        <VaultPalette
          mode={palette.mode}
          docs={docs}
          commands={commands}
          onClose={() => setPalette(null)}
          onOpenDoc={openDoc}
          onRunCommand={(c) => c.run()}
        />
      )}
    </>
  );
}

// ----- Recursive tree node removed: replaced with flattenTree() above for Babel compat -----

function FolderRow({ node, depth, isOpen, onToggle, onContextMenu, renaming, onRenameSubmit, onRenameCancel, moveDoc, moveFolder }) {
  const [over, setOver] = useState(false);
  const Icon = isOpen ? FolderOpen : FolderClosed;
  return (
    <div
      className="vault-row"
      data-testid={`folder-${node.path}`}
      onContextMenu={onContextMenu}
      onClick={onToggle}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "move"; setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        try {
          const data = JSON.parse(e.dataTransfer.getData("application/x-disciple") || "{}");
          if (data.kind === "doc") moveDoc(data.id, node.path);
          else if (data.kind === "folder") moveFolder(data.path, node.path);
        } catch {}
      }}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        paddingLeft: 6 + depth * 12, paddingRight: 8,
        paddingTop: 4, paddingBottom: 4,
        cursor: "pointer", userSelect: "none",
        fontSize: 12.5, color: "var(--ink-soft)",
        background: over ? "rgba(76,214,190,0.14)" : "transparent",
        borderRadius: 6,
        margin: "1px 4px",
      }}
    >
      <span
        className="vault-grip"
        draggable
        onClick={(e) => e.stopPropagation()}
        onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData("application/x-disciple", JSON.stringify({ kind: "folder", path: node.path })); e.dataTransfer.effectAllowed = "move"; }}
        title="Drag to move"
        data-testid={`drag-folder-${node.path}`}
      >
        <GripVertical size={12} />
      </span>
      {isOpen ? <ChevronDown size={11} style={{ color: "var(--ink-dim)", flexShrink: 0 }} /> : <ChevronRight size={11} style={{ color: "var(--ink-dim)", flexShrink: 0 }} />}
      <Icon size={13} style={{ color: "var(--ink-dim)", flexShrink: 0 }} />
      {renaming ? (
        <RenameInput defaultValue={node.name} onSubmit={onRenameSubmit} onCancel={onRenameCancel} />
      ) : (
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
      )}
    </div>
  );
}

function DocRow({ doc, depth, active, onOpen, onContextMenu, renaming, onRenameSubmit, onRenameCancel, moveDoc }) {
  return (
    <div
      className="vault-row"
      data-testid={`vault-doc-${doc.id}`}
      onContextMenu={(e) => onContextMenu(e, "doc", doc)}
      onClick={renaming ? undefined : onOpen}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        paddingLeft: 6 + depth * 12 + 17,
        paddingRight: 8, paddingTop: 4, paddingBottom: 4,
        cursor: "pointer", userSelect: "none",
        fontSize: 12.5,
        color: active ? "var(--ink)" : "var(--ink-soft)",
        background: active ? "var(--panel-2)" : "transparent",
        borderLeft: active ? "2px solid var(--ph-product-teal)" : "2px solid transparent",
        borderRadius: 6,
        margin: "1px 4px",
      }}
    >
      <span
        className="vault-grip"
        draggable
        onClick={(e) => e.stopPropagation()}
        onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData("application/x-disciple", JSON.stringify({ kind: "doc", id: doc.id })); e.dataTransfer.effectAllowed = "move"; }}
        title="Drag to move"
        data-testid={`drag-doc-${doc.id}`}
      >
        <GripVertical size={12} />
      </span>
      <FileText size={12} style={{ color: active ? "var(--ph-product-teal)" : "var(--ink-dim)", flexShrink: 0 }} />
      {renaming ? (
        <RenameInput defaultValue={doc.name} onSubmit={onRenameSubmit} onCancel={onRenameCancel} />
      ) : (
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</span>
      )}
    </div>
  );
}

function RenameInput({ defaultValue, onSubmit, onCancel }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <input
      ref={ref}
      defaultValue={defaultValue}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); onSubmit(e.currentTarget.value.trim()); }
        else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      }}
      onBlur={(e) => onSubmit(e.currentTarget.value.trim())}
      style={{
        background: "var(--panel-3)", border: "1px solid var(--cobalt-bright)",
        color: "var(--ink)", fontSize: 12.5, padding: "1px 6px",
        borderRadius: 4, outline: "none", flex: 1, minWidth: 0,
      }}
      data-testid="rename-input"
    />
  );
}
